import {ResearchState, HousingCorporation} from '$src/bootstrap/Store'
import {Component, React, observer, resolveCss} from '$src/handy'
import * as css from './AllTabsSummary.css'
import {ResearchStateTab, ComparableSale} from '$src/bootstrap/Store'
import {getSnapshot} from 'mobx-state-tree'
import {mapValues} from 'lodash'
import * as math from 'mathjs'

interface Props {
  css?: any
  researchState: typeof ResearchState.Type
  housingCorporation: typeof HousingCorporation.Type
  toggleRentData: () => void
  rentTabState: boolean
}

interface State {}

const classes = resolveCss(css, {})

export const getDataForTabs = (
  tabs: Array<typeof ResearchStateTab.Type>,
  housingCorporation: typeof HousingCorporation.Type,
): Array<any> => {
  const byNumberOfRooms = {
    '1': [] as Array<typeof ComparableSale.SnapshotType>,
    '2': [] as Array<typeof ComparableSale.SnapshotType>,
    '3': [] as Array<typeof ComparableSale.SnapshotType>,
    '4+': [] as Array<typeof ComparableSale.SnapshotType>,
    all: [] as Array<typeof ComparableSale.SnapshotType>,
    unknown: [] as Array<typeof ComparableSale.SnapshotType>,
  }

  for (const tab of tabs) {
    if (!tab.enabled) continue
    const {foundItems} = tab
    const byId = getSnapshot(foundItems.byId)

    for (const roomId in byId) {
      if (byId[roomId].dataType === 'premise') {
        if (!tab.foundItems.selectedIds.has(roomId)) continue
        const room: typeof ComparableSale.SnapshotType = byId[roomId]
        const numberOfRooms =
          typeof room.numberOfRooms === 'number'
            ? room.numberOfRooms
            : typeof room.numberOfRooms === 'string'
            ? parseInt(room.numberOfRooms, 10)
            : 0
        if (
          numberOfRooms === 0 ||
          isNaN(numberOfRooms) ||
          numberOfRooms > 3000
        ) {
          byNumberOfRooms.unknown.push(room)
        } else if (numberOfRooms >= 1 && numberOfRooms <= 3) {
          // @ts-ignore intentional
          byNumberOfRooms[String(numberOfRooms)].push(room)
        } else {
          byNumberOfRooms['4+'].push(room)
        }

        byNumberOfRooms.all.push(room)
      }

      if (!byId[roomId].dataType) {
        if (!tab.foundItems.selectedIdsOldData.has(roomId)) continue
        const room: typeof ComparableSale.SnapshotType = byId[roomId]
        const numberOfRooms =
          typeof room.numberOfRooms === 'number'
            ? room.numberOfRooms
            : typeof room.numberOfRooms === 'string'
            ? parseInt(room.numberOfRooms, 10)
            : 0
        if (
          numberOfRooms === 0 ||
          isNaN(numberOfRooms) ||
          numberOfRooms > 3000
        ) {
          byNumberOfRooms.unknown.push(room)
        } else if (numberOfRooms >= 1 && numberOfRooms <= 3) {
          // @ts-ignore intentional
          byNumberOfRooms[String(numberOfRooms)].push(room)
        } else {
          byNumberOfRooms['4+'].push(room)
        }

        byNumberOfRooms.all.push(room)
      }
    }
  }

  const dataByNumberOfRooms: any = mapValues(
    byNumberOfRooms,
    (a: Array<typeof ComparableSale.SnapshotType>, numberOfRooms: number) => {
      let prices: any[]

      if (housingCorporation && housingCorporation.isSummaryAdjusted()) {
        prices = a
          .map(b => b.priceSqmAdjusted)
          .filter(b => typeof b === 'number')
          .sort()
      } else {
        prices = a
          .map(b => b.priceSqm)
          .filter(b => typeof b === 'number')
          .sort()
      }

      if (prices.length === 0) {
        return {
          firstQuartile: '',
          median: '',
          thirdQuartile: '',
          averagePrice: '',
          total: a.length,
          numberOfRooms: numberOfRooms,
          averageArea: '',
        }
      }
      const [firstQuartile, median, thirdQuartile] = math.quantileSeq(prices, [
        0.25,
        0.5,
        0.75,
      ]) as any

      const averagePrice = prices.reduce((a, b) => a + b) / prices.length
      const livingAreas = a
        .map(b => b.livingArea)
        .filter(b => typeof b === 'number' || parseFloat(b) === parseFloat(b))
        .map(parseFloat)

      const averageArea =
        livingAreas.length === 0
          ? ''
          : livingAreas.reduce((a, b) => a + b, 0) / livingAreas.length
      return {
        firstQuartile: formatNumber(Math.round(firstQuartile)),
        median: formatNumber(Math.round(median)),
        thirdQuartile: formatNumber(Math.round(thirdQuartile)),
        averagePrice: formatNumber(Math.round(averagePrice)),
        total: a.length,
        numberOfRooms,
        averageArea:
          typeof averageArea !== 'number'
            ? averageArea
            : formatNumber((Math.round(averageArea * 100) / 100).toFixed(1)),
      }
    },
  )

  const data = Object.keys(dataByNumberOfRooms).map(
    (n: any) => dataByNumberOfRooms[n],
  )
  return data
}

export const getDataForTabsRent = (
  tabs: Array<typeof ResearchStateTab.Type>,
  housingCorporation: typeof HousingCorporation.Type,
): Array<any> => {
  const byNumberOfRooms = {
    '1': [] as Array<typeof ComparableSale.SnapshotType>,
    '2': [] as Array<typeof ComparableSale.SnapshotType>,
    '3': [] as Array<typeof ComparableSale.SnapshotType>,
    '4+': [] as Array<typeof ComparableSale.SnapshotType>,
    all: [] as Array<typeof ComparableSale.SnapshotType>,
    unknown: [] as Array<typeof ComparableSale.SnapshotType>,
  }

  for (const tab of tabs) {
    if (!tab.enabled) continue
    const {foundItems} = tab
    const byId = getSnapshot(foundItems.byId)

    for (const roomId in byId) {
      if (byId[roomId].dataType === 'rent') {
        if (!tab.foundItems.selectedIdsRent.has(roomId)) continue
        const room: typeof ComparableSale.SnapshotType = byId[roomId]
        const numberOfRooms =
          typeof room.numberOfRooms === 'number'
            ? room.numberOfRooms
            : typeof room.numberOfRooms === 'string'
            ? parseInt(room.numberOfRooms, 10)
            : 0
        if (
          numberOfRooms === 0 ||
          isNaN(numberOfRooms) ||
          numberOfRooms > 3000
        ) {
          byNumberOfRooms.unknown.push(room)
        } else if (numberOfRooms >= 1 && numberOfRooms <= 3) {
          // @ts-ignore intentional
          byNumberOfRooms[String(numberOfRooms)].push(room)
        } else {
          byNumberOfRooms['4+'].push(room)
        }

        byNumberOfRooms.all.push(room)
      }
    }
  }

  const dataByNumberOfRooms: any = mapValues(
    byNumberOfRooms,
    (a: Array<typeof ComparableSale.SnapshotType>, numberOfRooms: number) => {
      let prices: any[]
      prices = a
        .map(b => b.rentSqm)
        .filter(b => typeof b === 'number')
        .sort()

      if (prices.length === 0) {
        return {
          firstQuartile: '',
          median: '',
          thirdQuartile: '',
          averagePrice: '',
          total: a.length,
          numberOfRooms: numberOfRooms,
          averageArea: '',
        }
      }
      const [firstQuartile, median, thirdQuartile] = math.quantileSeq(prices, [
        0.25,
        0.5,
        0.75,
      ]) as any

      const averagePrice = prices.reduce((a, b) => a + b) / prices.length
      const livingAreas = a
        .map(b => b.livingArea)
        .filter(b => typeof b === 'number' || parseFloat(b) === parseFloat(b))
        .map(parseFloat)

      const averageArea =
        livingAreas.length === 0
          ? ''
          : livingAreas.reduce((a, b) => a + b, 0) / livingAreas.length
      return {
        firstQuartile: formatNumber(firstQuartile.toFixed(1)),
        median: formatNumber(median.toFixed(1)),
        thirdQuartile: formatNumber(thirdQuartile.toFixed(1)),
        averagePrice: formatNumber(averagePrice.toFixed(1)),
        total: a.length,
        numberOfRooms,
        averageArea:
          typeof averageArea !== 'number'
            ? averageArea
            : formatNumber((Math.round(averageArea * 100) / 100).toFixed(1)),
      }
    },
  )

  const data = Object.keys(dataByNumberOfRooms).map(
    (n: any) => dataByNumberOfRooms[n],
  )
  return data
}

export const getDataPricesForTabs = (
  tabs: Array<typeof ResearchStateTab.Type>,
  housingCorporation: typeof HousingCorporation.Type,
): Array<any> => {
  const byNumberOfRooms = {
    '1': [] as Array<typeof ComparableSale.SnapshotType>,
    '2': [] as Array<typeof ComparableSale.SnapshotType>,
    '3': [] as Array<typeof ComparableSale.SnapshotType>,
    '4+': [] as Array<typeof ComparableSale.SnapshotType>,
    all: [] as Array<typeof ComparableSale.SnapshotType>,
    unknown: [] as Array<typeof ComparableSale.SnapshotType>,
  }

  for (const tab of tabs) {
    if (!tab.enabled) continue
    const {foundItems} = tab
    const byId = getSnapshot(foundItems.byId)

    for (const roomId in byId) {
      if (byId[roomId].dataType === 'premise') {
        if (!tab.foundItems.selectedIds.has(roomId)) continue
        const room: typeof ComparableSale.SnapshotType = byId[roomId]
        const numberOfRooms =
          typeof room.numberOfRooms === 'number'
            ? room.numberOfRooms
            : typeof room.numberOfRooms === 'string'
            ? parseInt(room.numberOfRooms, 10)
            : 0
        if (
          numberOfRooms === 0 ||
          isNaN(numberOfRooms) ||
          numberOfRooms > 3000
        ) {
          byNumberOfRooms.unknown.push(room)
        } else if (numberOfRooms >= 1 && numberOfRooms <= 3) {
          // @ts-ignore intentional
          byNumberOfRooms[String(numberOfRooms)].push(room)
        } else {
          byNumberOfRooms['4+'].push(room)
        }

        byNumberOfRooms.all.push(room)
      }

      if (!byId[roomId].dataType) {
        if (!tab.foundItems.selectedIdsOldData.has(roomId)) continue
        const room: typeof ComparableSale.SnapshotType = byId[roomId]
        const numberOfRooms =
          typeof room.numberOfRooms === 'number'
            ? room.numberOfRooms
            : typeof room.numberOfRooms === 'string'
            ? parseInt(room.numberOfRooms, 10)
            : 0
        if (
          numberOfRooms === 0 ||
          isNaN(numberOfRooms) ||
          numberOfRooms > 3000
        ) {
          byNumberOfRooms.unknown.push(room)
        } else if (numberOfRooms >= 1 && numberOfRooms <= 3) {
          // @ts-ignore intentional
          byNumberOfRooms[String(numberOfRooms)].push(room)
        } else {
          byNumberOfRooms['4+'].push(room)
        }

        byNumberOfRooms.all.push(room)
      }
    }
  }

  const dataByNumberOfRooms: any = mapValues(
    byNumberOfRooms,
    (a: Array<typeof ComparableSale.SnapshotType>) => {
      let prices: any[]

      if (housingCorporation && housingCorporation.isSummaryAdjusted()) {
        prices = a
          .map(b => b.priceSqmAdjusted)
          .filter(b => typeof b === 'number')
          .sort()
      } else {
        prices = a
          .map(b => b.priceSqm)
          .filter(b => typeof b === 'number')
          .sort()
      }

      if (prices.length === 0) {
        return {
          current: '',
          firstQuartileAgo: '',
          secondQuartileAgo: '',
          thirdQuartileAgo: '',
          fourthQuartileAgo: '',
        }
      }

      const [median] = math.quantileSeq(prices, [0.5]) as any

      let q1Array: any[] = []
      let q2Array: any[] = []
      let q3Array: any[] = []
      let q4Array: any[] = []

      a.forEach(b => {
        const q1calculate = b.priceSqmAdjusted1QuarterAgo
        q1Array.push(q1calculate)

        const q2calculate = b.priceSqmAdjusted2QuartersAgo
        q2Array.push(q2calculate)

        const q3calculate = b.priceSqmAdjusted3QuartersAgo
        q3Array.push(q3calculate)

        const q4calculate = b.priceSqmAdjusted4QuartersAgo
        q4Array.push(q4calculate)
      })

      // first quartile median
      let firstQuartileMedian
      const q1sorted = Array.from(q1Array).sort((a, b) => a - b)
      const q1middle = Math.floor(q1sorted.length / 2)
      if (q1sorted.length % 2 === 0) {
        firstQuartileMedian = (q1sorted[q1middle - 1] + q1sorted[q1middle]) / 2
      } else {
        firstQuartileMedian = q1sorted[q1middle]
      }

      // second quartile median
      let secondQuartileMedian
      const q2sorted = Array.from(q2Array).sort((a, b) => a - b)
      const q2middle = Math.floor(q2sorted.length / 2)
      if (q2sorted.length % 2 === 0) {
        secondQuartileMedian = (q2sorted[q2middle - 1] + q2sorted[q2middle]) / 2
      } else {
        secondQuartileMedian = q2sorted[q2middle]
      }

      // third quartile median
      let thirdQuartileMedian
      const q3sorted = Array.from(q3Array).sort((a, b) => a - b)
      const q3middle = Math.floor(q3sorted.length / 2)
      if (q3sorted.length % 2 === 0) {
        thirdQuartileMedian = (q3sorted[q3middle - 1] + q3sorted[q3middle]) / 2
      } else {
        thirdQuartileMedian = q3sorted[q3middle]
      }

      // fourth quartile median
      let fourthQuartileMedian
      const q4sorted = Array.from(q4Array).sort((a, b) => a - b)
      const q4middle = Math.floor(q4sorted.length / 2)
      if (q4sorted.length % 2 === 0) {
        fourthQuartileMedian = (q4sorted[q4middle - 1] + q4sorted[q4middle]) / 2
      } else {
        fourthQuartileMedian = q4sorted[q4middle]
      }

      return {
        current: formatNumber(Math.round(median)),
        firstQuartileAgo: formatNumber(Math.round(firstQuartileMedian)),
        secondQuartileAgo: formatNumber(Math.round(secondQuartileMedian)),
        thirdQuartileAgo: formatNumber(Math.round(thirdQuartileMedian)),
        fourthQuartileAgo: formatNumber(Math.round(fourthQuartileMedian)),
      }
    },
  )

  const data = Object.keys(dataByNumberOfRooms).map(
    (n: any) => dataByNumberOfRooms[n],
  )
  return data
}

// @todo use Intl.NumberFormat instead
const formatNumber = (n: number | string): string => {
  return String(n).replace(/\./, ',')
}

@observer
export default class AllTabsSummary extends Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context)
    this.state = {}
  }

  render() {
    const {props} = this
    const data = getDataForTabs(
      props.researchState.tabs.list,
      props.housingCorporation,
    )

    const rentData = getDataForTabsRent(
      props.researchState.tabs.list,
      props.housingCorporation,
    )

    const dataPrices = getDataPricesForTabs(
      props.researchState.tabs.list,
      props.housingCorporation,
    )

    return (
      <div {...classes('root')}>
        <div {...classes('leftTable')}>
          {/* PRICE TABLE */}
          {!props.rentTabState && (
            <>
              <h4>Price information</h4>
              <table {...classes('table')}>
                <thead>
                  <tr>
                    {columns.map(col => {
                      return <th key={col.accessor}>{col.Header}</th>
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => {
                    return <Row key={'row-' + i} row={row} columns={columns} />
                  })}
                </tbody>
              </table>
            </>
          )}

          {/* RENT TABLE */}
          {props.rentTabState && (
            <>
              <h4>Rent information</h4>
              <table {...classes('table')}>
                <thead>
                  <tr>
                    {columns.map(col => {
                      return <th key={col.accessor}>{col.Header}</th>
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rentData.map((row, i) => {
                    return <Row key={'row-' + i} row={row} columns={columns} />
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
        {!props.rentTabState && (
          <div {...classes('rightTable')}>
            {/* INDEXED PRICES TABLE */}
            <h4>Development of indexed median price/sqm</h4>

            <table {...classes('table')}>
              <thead>
                <tr>
                  {columnsPrices.map(col => {
                    return <th key={col.accessor}>{col.Header}</th>
                  })}
                </tr>
              </thead>
              <tbody>
                {dataPrices.map((row, i) => {
                  return (
                    <Row key={'row-' + i} row={row} columns={columnsPrices} />
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }
}

const Row = (props: {
  row: {[s: string]: string}
  columns: Array<{accessor: string; Header: string}>
}) => {
  return (
    <tr>
      {props.columns.map(col => {
        return <td key={'col-' + col.accessor}>{props.row[col.accessor]}</td>
      })}
    </tr>
  )
}

export const columns = [
  {accessor: 'numberOfRooms', Header: 'Number of rooms'},
  {accessor: 'firstQuartile', Header: 'First quartile'},
  {accessor: 'median', Header: 'Median'},
  {accessor: 'thirdQuartile', Header: 'Third quartile'},
  {accessor: 'averagePrice', Header: 'Average'},
  {accessor: 'averageArea', Header: 'Average area'},
  {accessor: 'total', Header: 'Total items'},
]

export const columnsPrices = [
  {accessor: 'current', Header: 'Current'},
  {accessor: 'firstQuartileAgo', Header: '1Q ago'},
  {accessor: 'secondQuartileAgo', Header: '2Q ago'},
  {accessor: 'thirdQuartileAgo', Header: '3Q ago'},
  {accessor: 'fourthQuartileAgo', Header: '4Q ago'},
]



// WEBPACK FOOTER //
// ./src/comparableSales/AllTabsSummary.tsx